import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2, GraduationCap, Check, Star, Zap, Crown,
  ArrowRight, Briefcase, Users, Search, Award, TrendingUp,
  BarChart3, Target, Shield, Sparkles
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PricingPage = () => {
  const [activeTab, setActiveTab] = useState('company');
  const [billingCycle, setBillingCycle] = useState('yearly');
  const [companyPlans, setCompanyPlans] = useState([]);
  const [collegePlans, setCollegePlans] = useState([]);
  const [payPerUse, setPayPerUse] = useState({});

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const [companyRes, collegeRes, ppuRes] = await Promise.all([
        axios.get(`${API}/pricing/company-plans`),
        axios.get(`${API}/pricing/college-plans`),
        axios.get(`${API}/pricing/pay-per-use`)
      ]);
      setCompanyPlans(companyRes.data.plans || []);
      setCollegePlans(collegeRes.data.plans || []);
      setPayPerUse(ppuRes.data.pricing || {});
    } catch (err) {
      console.error('Failed to fetch pricing:', err);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const getFeatureValue = (value) => {
    if (value === -1) return 'Unlimited';
    if (value === true) return <Check className="text-emerald-400" size={18} />;
    if (value === false) return <span className="text-gray-600">—</span>;
    return value;
  };

  const getPlanIcon = (planId) => {
    const icons = {
      starter: <Zap className="text-cyan-400" size={24} />,
      growth: <Star className="text-purple-400" size={24} />,
      enterprise: <Crown className="text-amber-400" size={24} />,
      basic: <Shield className="text-cyan-400" size={24} />,
      professional: <TrendingUp className="text-purple-400" size={24} />,
      premium: <Sparkles className="text-amber-400" size={24} />
    };
    return icons[planId] || <Star size={24} />;
  };

  const companyFeatureLabels = {
    job_posts_per_month: 'Job Posts / Month',
    hiring_challenges_per_month: 'Hiring Challenges / Month',
    talent_search_unlocks: 'Talent Search Unlocks',
    featured_listings: 'Featured Listings',
    campus_campaigns: 'Campus Campaigns',
    support: 'Support Level'
  };

  const collegeFeatureLabels = {
    dashboard_access: 'Dashboard Access',
    student_analytics: 'Student Analytics',
    placement_reports: 'Placement Reports',
    talent_reports: 'Talent Reports / Month',
    platform_branding: 'Platform Branding',
    api_access: 'API Access',
    support: 'Support Level'
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-purple-500/5 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 py-20 relative">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Simple, Transparent <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500">Pricing</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
              Choose the plan that fits your hiring needs. Access India's premier developer talent pool.
            </p>

            {/* Tab Switcher */}
            <div className="inline-flex bg-white/5 rounded-xl p-1 mb-8">
              <button
                onClick={() => setActiveTab('company')}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
                  activeTab === 'company'
                    ? 'bg-gradient-to-r from-cyan-500 to-purple-600 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
                data-testid="company-tab"
              >
                <Building2 size={20} />
                For Companies
              </button>
              <button
                onClick={() => setActiveTab('college')}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
                  activeTab === 'college'
                    ? 'bg-gradient-to-r from-cyan-500 to-purple-600 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
                data-testid="college-tab"
              >
                <GraduationCap size={20} />
                For Colleges
              </button>
            </div>

            {/* Billing Cycle Toggle */}
            <div className="flex items-center justify-center gap-4 mb-12">
              <span className={`text-sm ${billingCycle === 'monthly' ? 'text-white' : 'text-gray-500'}`}>Monthly</span>
              <button
                onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
                className={`relative w-14 h-7 rounded-full transition-colors ${
                  billingCycle === 'yearly' ? 'bg-emerald-500' : 'bg-gray-600'
                }`}
                data-testid="billing-toggle"
              >
                <div className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
                  billingCycle === 'yearly' ? 'translate-x-8' : 'translate-x-1'
                }`} />
              </button>
              <span className={`text-sm ${billingCycle === 'yearly' ? 'text-white' : 'text-gray-500'}`}>
                Yearly <span className="text-emerald-400 text-xs ml-1">Save 17%</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="max-w-7xl mx-auto px-6 pb-20">
        <div className="grid md:grid-cols-3 gap-8">
          {(activeTab === 'company' ? companyPlans : collegePlans).map((plan, idx) => {
            const price = billingCycle === 'yearly' ? plan.price_yearly : plan.price_monthly;
            const isPopular = idx === 1;
            const featureLabels = activeTab === 'company' ? companyFeatureLabels : collegeFeatureLabels;

            return (
              <div
                key={plan.id}
                className={`relative rounded-2xl p-6 transition-all hover:scale-[1.02] ${
                  isPopular
                    ? 'bg-gradient-to-br from-purple-500/20 to-cyan-500/10 border-2 border-purple-500/50'
                    : 'bg-white/5 border border-white/10'
                }`}
                data-testid={`plan-card-${plan.id}`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-purple-500 to-cyan-500 text-white text-xs font-bold px-4 py-1 rounded-full">
                      MOST POPULAR
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <div className="inline-flex p-3 bg-white/5 rounded-xl mb-4">
                    {getPlanIcon(plan.id)}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                  <p className="text-gray-400 text-sm">{plan.description}</p>
                </div>

                <div className="text-center mb-6">
                  <div className="text-4xl font-bold text-white">
                    {formatPrice(price)}
                  </div>
                  <p className="text-gray-500 text-sm">
                    /{billingCycle === 'yearly' ? 'year' : 'month'}
                  </p>
                </div>

                <div className="space-y-3 mb-6">
                  {Object.entries(plan.features).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between text-sm">
                      <span className="text-gray-400">{featureLabels[key] || key}</span>
                      <span className="text-white font-medium">{getFeatureValue(value)}</span>
                    </div>
                  ))}
                </div>

                <Link
                  to={activeTab === 'company' ? '/company/auth' : '/college/auth'}
                  className={`block w-full text-center py-3 rounded-xl font-semibold transition-all ${
                    isPopular
                      ? 'bg-gradient-to-r from-purple-500 to-cyan-500 text-white hover:opacity-90'
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  Get Started
                  <ArrowRight className="inline ml-2" size={16} />
                </Link>
              </div>
            );
          })}
        </div>

        {/* Pay-per-use Section */}
        {activeTab === 'company' && (
          <div className="mt-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Pay As You Go</h2>
              <p className="text-gray-400">No subscription? No problem. Pay only for what you need.</p>
            </div>

            <div className="grid md:grid-cols-5 gap-4">
              {Object.entries(payPerUse).map(([key, item]) => (
                <div
                  key={key}
                  className="bg-white/5 border border-white/10 rounded-xl p-4 text-center hover:border-cyan-500/50 transition-colors"
                >
                  <p className="text-2xl font-bold text-cyan-400 mb-1">{formatPrice(item.price)}</p>
                  <p className="text-gray-400 text-sm">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Features Comparison */}
        <div className="mt-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Why Choose CodeArena?</h2>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-white/5 rounded-xl p-6 text-center">
              <div className="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Users className="text-cyan-400" size={24} />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">10,000+ Developers</h3>
              <p className="text-gray-400 text-sm">Access India's largest verified developer talent pool</p>
            </div>
            <div className="bg-white/5 rounded-xl p-6 text-center">
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Target className="text-purple-400" size={24} />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">AI-Powered Matching</h3>
              <p className="text-gray-400 text-sm">Athena AI powered candidate recommendations</p>
            </div>
            <div className="bg-white/5 rounded-xl p-6 text-center">
              <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Award className="text-emerald-400" size={24} />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Verified Skills</h3>
              <p className="text-gray-400 text-sm">All developers are skill-tested on our platform</p>
            </div>
            <div className="bg-white/5 rounded-xl p-6 text-center">
              <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="text-amber-400" size={24} />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Rich Analytics</h3>
              <p className="text-gray-400 text-sm">Deep insights into hiring funnel & candidate quality</p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-20 text-center bg-gradient-to-r from-cyan-500/10 to-purple-500/10 rounded-3xl p-12 border border-white/10">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Find Top Talent?</h2>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">
            Join 100+ companies already hiring through EvalentX Arena. Start with a free trial today.
          </p>
          <Link
            to="/company/auth"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-cyan-500 to-purple-600 text-white rounded-xl font-semibold hover:opacity-90 transition-opacity"
          >
            Start Free Trial
            <ArrowRight size={20} />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
